import type { SessionSummary } from '../services/api';

type SetAvailableSessions = (
  updater: (sessions: SessionSummary[]) => SessionSummary[]
) => void;

interface UpsertSessionSummaryNameArgs {
  sessions: SessionSummary[];
  sessionId: string;
  sessionName: string;
  agentId?: string | null;
}

interface ApplySuccessfulSessionNameArgs {
  targetSessionId: string;
  sessionName: string;
  agentId?: string | null;
  setSessionName: (name: string) => void;
  setSessionNameInput: (name: string) => void;
  closeSessionNameModal: () => void;
  setAvailableSessions: SetAvailableSessions;
  onAfterClose?: () => void;
}

export const normalizeSessionName = (sessionName?: string | null): string =>
  (sessionName || '').trim();

export const shouldShowSessionNameModal = (sessionName?: string | null): boolean =>
  normalizeSessionName(sessionName).length === 0;

export const getWorkspaceDisplayName = (sessionName?: string | null): string => {
  const normalizedSessionName = normalizeSessionName(sessionName);
  if (normalizedSessionName) {
    return normalizedSessionName;
  }
  return 'Untitled workspace';
};

export const upsertSessionSummaryName = ({
  sessions,
  sessionId,
  sessionName,
  agentId,
}: UpsertSessionSummaryNameArgs): SessionSummary[] => {
  let found = false;
  const nextSessions = sessions.map((session) => {
    if (session.session_id !== sessionId) {
      return session;
    }

    found = true;
    return {
      ...session,
      session_name: sessionName,
      agent_id: session.agent_id ?? agentId ?? null,
    };
  });

  if (found) {
    return nextSessions;
  }

  const now = new Date().toISOString();
  return [
    {
      session_id: sessionId,
      session_name: sessionName,
      created_at: now,
      last_active_at: now,
      report_count: 0,
      stage: 'brief_builder_draft',
      agent_id: agentId ?? null,
    },
    ...sessions,
  ];
};

export const applySuccessfulSessionName = async ({
  targetSessionId,
  sessionName,
  agentId,
  setSessionName,
  setSessionNameInput,
  closeSessionNameModal,
  setAvailableSessions,
  onAfterClose,
}: ApplySuccessfulSessionNameArgs): Promise<void> => {
  setSessionName(sessionName);
  setSessionNameInput(sessionName);
  closeSessionNameModal();
  setAvailableSessions((sessions) =>
    upsertSessionSummaryName({
      sessions,
      sessionId: targetSessionId,
      sessionName,
      agentId,
    })
  );
  if (onAfterClose) {
    onAfterClose();
  }
};
